import { useState } from 'react';
import { Link } from 'react-router-dom';

const ACK_KEY = 'eceap-notice-ack';

/**
 * A notice, deliberately, rather than an accept/reject consent prompt.
 *
 * The distinction is not pedantry. Consent has to be a real choice, and
 * there is nothing here to choose: this site sets no cookies, runs no
 * analytics and carries no trackers. What it stores is the sign-in
 * token, a theme preference, and a cached copy of its own files — the
 * first of which the site cannot function without, and none of which is
 * used to follow anyone. A "Reject" button would either do nothing or
 * break sign-in, and a banner that asks permission for something it
 * proceeds to do regardless teaches people that the button is a
 * formality.
 *
 * So this tells the reader what is stored, links to the detail, and gets
 * out of the way. If tracking or analytics are ever added, that changes:
 * those need opt-in consent obtained before they load, and this
 * component would have to become a real gate rather than being edited to
 * mention them.
 */
const ConsentBanner = () => {
  // Read once on mount. Wrapped because storage access throws outright
  // in a browser with site data blocked, and a notice about storage is a
  // poor place to crash the whole app over storage.
  const [acknowledged, setAcknowledged] = useState(() => {
    try {
      return localStorage.getItem(ACK_KEY) === 'true';
    } catch {
      // Unreadable storage means an unwritable one too, so the banner
      // would return on every page load. Staying quiet is the better
      // failure: the notice is also linked in the footer.
      return true;
    }
  });

  if (acknowledged) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(ACK_KEY, 'true');
    } catch {
      // Dismissed for this visit at least.
    }
    setAcknowledged(true);
  };

  return (
    <div
      role="region"
      aria-label="Notice about site data"
      style={{
        position: 'fixed',
        left: '1rem',
        right: '1rem',
        bottom: '1rem',
        zIndex: 1000,
        maxWidth: '44rem',
        margin: '0 auto',
        padding: '1rem 1.25rem',
        background: 'var(--card-bg)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.25)',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '0.75rem',
        fontSize: '0.85rem'
      }}
    >
      <p style={{ margin: 0, flex: '1 1 20rem', minWidth: 0 }}>
        This site uses <strong>no cookies and no trackers</strong>. It stores only what keeps you signed in,
        your theme choice, and a copy of its own files so it works offline.{' '}
        <Link to="/privacy">What's stored and why</Link> &middot; <Link to="/terms">Terms</Link>
      </p>
      <button type="button" className="btn btn-outline btn-sm" onClick={dismiss} style={{ flex: '0 0 auto' }}>
        Got it
      </button>
    </div>
  );
};

export default ConsentBanner;
